import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Check, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, api } from "@/lib/api";
import { HubGate, HubMessage } from "@/pages/hub/Hub";

export default function HubSection() {
  const { sectionSlug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    api
      .get("/hub")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "error"));
  }, [user]);

  if (user === false) {
    return (
      <HubMessage title="Sign in to your hub" body="Sign in with the email you purchased with."
        linkTo="/account" linkText="Sign in" testId="hubsection-gate-signin" />
    );
  }
  if (error) return <HubGate error={error} />;
  if (!data) {
    return <div className="mx-auto max-w-5xl px-4 py-24 text-sm uppercase tracking-widest text-slate-500">Loading…</div>;
  }

  const section = data.sections.find((s) => s.slug === sectionSlug);
  if (!section) {
    return (
      <HubMessage title="Section not found" body="That section doesn't exist in this pack."
        linkTo="/hub" linkText="Back to hub" testId="hubsection-notfound" />
    );
  }

  const readSet = new Set(data.read || []);
  const reads = section.resources.filter((r) => r.kind === "read");
  const downloads = section.resources.filter((r) => r.kind === "download");

  return (
    <div data-testid={`hub-section-page-${section.slug}`} className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      <Link
        data-testid="back-to-hub"
        to="/hub"
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-400 transition-colors hover:text-[#FF5500]"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Job Seeker Hub
      </Link>
      <div className="mt-6 flex items-baseline gap-4">
        <span className="font-display text-5xl font-extrabold text-[#FF5500]">{section.number}</span>
        <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-5xl">
          {section.name}
        </h1>
      </div>

      {reads.length > 0 && (
        <>
          <div className="mono-label mt-12">Read online</div>
          <div className="mt-4 divide-y divide-white/10 border border-white/10 bg-[#121418]">
            {reads.map((r) => {
              const isRead = readSet.has(r.id);
              return (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-5 w-5 items-center justify-center border ${
                        isRead ? "border-[#FF5500] bg-[#FF5500]" : "border-white/20"
                      }`}
                    >
                      {isRead && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span className="text-sm font-medium text-slate-200">{r.title}</span>
                    {r.tasks > 0 && (
                      <span className="font-mono-ind text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        {(data.checklist_done || {})[r.id] || 0}/{r.tasks} tasks
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      data-testid={`read-${r.id}`}
                      to={`/hub/${section.slug}/${r.id}`}
                      className="flex items-center gap-2 border border-[#FF5500] px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white"
                    >
                      <BookOpen className="h-3.5 w-3.5" /> Read online
                    </Link>
                    <a
                      data-testid={`download-${r.id}`}
                      href={`${API}/hub/file/${r.id}`}
                      className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-200"
                      title="Download PDF"
                    >
                      <Download className="h-3.5 w-3.5" /> PDF
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {downloads.length > 0 && (
        <>
          <div className="mono-label mt-12">Download</div>
          <div className="mt-4 divide-y divide-white/10 border border-white/10 bg-[#121418]">
            {downloads.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div>
                  <span className="text-sm font-medium text-slate-200">{r.title}</span>
                  <span className="font-mono-ind ml-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {r.format}
                  </span>
                </div>
                <a
                  data-testid={`download-${r.id}`}
                  href={`${API}/hub/file/${r.id}`}
                  className="flex items-center gap-2 bg-[#FF5500] px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
                >
                  <Download className="h-3.5 w-3.5" /> Download template
                </a>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
